// client/src/pages/admin/DevTeam.tsx
//
// Dev Team board — triage dashboard for user-submitted bug/error reports across
// the suite, laid out as a Kanban board (Open / In progress / Resolved). The dev
// team sets a status and assigns each to a person.
//
// Data source: the admin-only /api/dev-team backend (reads Feedback.BugReports
// direct-SQL cross-app; status/assign writes hop through func-shared-services,
// the sole writer). If that backend isn't reachable yet (env not provisioned,
// migration-005 not applied, or func not deployed), it falls back to the local
// preview store (BugReportsContext) and shows the Preview banner.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Terminal, Search, Clock, Trash2, Mail, StickyNote, MoreHorizontal, Mailbox, Layers, X, CheckSquare, Square, ChevronDown, ChevronRight, Paperclip } from 'lucide-react';
import { useAuth } from '../../auth/AuthContext';
import { quoteOriginal, withQuote } from '../../utils/emailQuote';
import { useBugReports, BugReport, BugStatus } from '../../contexts/BugReportsContext';
import { fetchBoardReports, fetchAssignees, updateReport, deleteReport, emailSender, saveNotes, fetchThread, ThreadMessage, mergeReports, ungroupReport, GROUP_CONTEXT_APP, attachmentDownloadUrl, AttachmentRef } from '../../services/devTeamApi';

const STATUSES: BugStatus[] = ['inbox_triage', 'open', 'in_progress', 'resolved'];

// Fallback assignee list for preview mode; live mode pulls DEV_TEAM_EMAILS ∪ ADMIN_EMAILS.
const FALLBACK_ASSIGNEES = ['hstaggs@bmss.com', 'deison@bmss.com', 'tcrawford@bmss.com', 'rwiginton@bmss.com'];

// "Archived" is a DERIVED state, not a stored status: a resolved report whose
// resolution (resolvedAt) is >= 14 days old. So archiving happens automatically
// and identically for every admin, with no backend write, timer, or schema change.
const ARCHIVE_DAYS = 14;
const ARCHIVE_MS = ARCHIVE_DAYS * 24 * 60 * 60 * 1000;
const SHOW_ARCHIVED_LS = 'devteam.showArchived.v1';

function isArchived(r: BugReport): boolean {
  if (r.status !== 'resolved') return false;
  const t = r.resolvedAt ? Date.parse(r.resolvedAt) : NaN;
  return Number.isFinite(t) && (Date.now() - t) >= ARCHIVE_MS;
}

type Column = { key: string; label: string; color: string };
const BASE_COLUMNS: Column[] = [
  { key: 'inbox_triage', label: 'Inbox Triage', color: '#a78bfa' },
  { key: 'open', label: 'Open', color: '#fbbf24' },
  { key: 'in_progress', label: 'In progress', color: '#64d2ff' },
  { key: 'resolved', label: 'Completed', color: 'var(--good)' },
];
const ARCHIVED_COLUMN: Column = { key: 'archived', label: 'Archived', color: 'var(--muted)' };

// PE staff directory row — a real individual employee, from
// /api/pe-staff-search?directory=1 (filtered to StaffIncludeInReports + active).
type PEStaff = { staffName: string; staffUser: string; staffEmail: string; staffOffice: string };

// Reports store the reporter as a bmss.com email (SubmittedBy); PE gives us
// StaffUser (a login, which may be an email or a short username). Match on full
// email OR local-part so either form lines up.
function reporterMatches(submittedBy: string, staffUser: string): boolean {
  const sb = (submittedBy || '').toLowerCase().trim();
  const su = (staffUser || '').toLowerCase().trim();
  if (!sb || !su) return false;
  if (sb === su) return true;
  return sb.split('@')[0] === su.split('@')[0];
}

function statusColor(s: BugStatus): string {
  switch (s) {
    case 'inbox_triage': return '#a78bfa';
    case 'open': return '#fbbf24';
    case 'in_progress': return '#64d2ff';
    case 'resolved': return 'var(--good)';
    default: return 'var(--muted)';
  }
}
function statusLabel(s: BugStatus): string {
  return ({ inbox_triage: 'Inbox Triage', open: 'Open', in_progress: 'In progress', resolved: 'Completed' } as Record<BugStatus, string>)[s];
}
const shortName = (email: string) => email.split('@')[0];

const selectStyle: React.CSSProperties = {
  padding: '5px 8px', borderRadius: 8, border: '1px solid var(--border)',
  background: 'transparent', color: 'inherit', fontSize: 12,
};

const fieldLabel: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12, color: 'var(--muted)' };
const fieldInput: React.CSSProperties = { padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'inherit', fontSize: 13 };
const btnGhost: React.CSSProperties = { background: 'none', border: '1px solid var(--border)', color: 'var(--muted)', borderRadius: 8, padding: '7px 14px', fontSize: 12.5, cursor: 'pointer' };
const btnPrimary = (disabled: boolean): React.CSSProperties => ({ background: disabled ? 'var(--border)' : 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 16px', fontSize: 12.5, fontWeight: 600, cursor: disabled ? 'default' : 'pointer' });

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Compose-and-send-to-reporter modal. Opened two ways: automatically when an
// item is moved to Completed (mode 'resolved'), or via the card's email icon at
// any status (mode 'manual'). Subject is pre-filled "Re: <issue title>" since
// this is always a reply to the reporter's original report; body starts BLANK
// (you write the whole message). Send hops through /api/dev-team → the shared
// Send-Email Logic App, sent AS the logged-in user so replies go straight to them.
//
// Resolved mode offers three distinct outcomes, not two:
//   - Send email       — sends, item stays Completed.
//   - Complete and don't send — item stays Completed, no email. (This is what
//     the lone "Cancel" button used to do, before there was a real Cancel.)
//   - Cancel            — reverts the item to its pre-completion status AND
//     doesn't send. Only offered in resolved mode, via onCancelComplete.
// Manual mode (opened from the card's email icon, not tied to a status change)
// keeps the plain two-button Send / Cancel — there's no completion to revert.
function EmailComposer({ report, mode, onClose, onCancelComplete }: {
  report: BugReport; mode: 'resolved' | 'manual'; onClose: () => void; onCancelComplete?: () => void;
}) {
  const [to, setTo] = useState(report.submittedBy && report.submittedBy !== 'unknown' ? report.submittedBy : '');
  const [subject, setSubject] = useState(`Re: ${report.issue}`);
  const [bodyText, setBodyText] = useState(''); // blank — no template
  const quote = quoteOriginal({ from: report.submittedBy, when: report.when, subject: report.issue, body: report.description });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const validEmail = EMAIL_RE.test(to.trim());
  const canSend = validEmail && subject.trim().length > 0 && bodyText.trim().length > 0 && !sending && !sent;

  const send = async () => {
    setErr(null); setSending(true);
    try {
      await emailSender({ bugReportId: report.bugReportId, to: to.trim(), subject: subject.trim(), body: withQuote(bodyText, quote) });
      setSent(true);
      setTimeout(onClose, 800);
    } catch (e: any) {
      setErr(e?.message || 'Could not send the email.');
      setSending(false);
    }
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.66)', backdropFilter: 'blur(2px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      {/* Solid, opaque surface — the shared .card background is translucent, which
          let the board bleed through; override it so the composer reads clearly. */}
      <div className="card" onClick={e => e.stopPropagation()} style={{ width: 'min(560px, 100%)', maxHeight: '90vh', overflow: 'auto', padding: 0, background: 'linear-gradient(180deg, #1a212c, #141922)' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Mail size={16} style={{ color: 'var(--accent)' }} />
          <strong style={{ fontSize: 14 }}>Email the reporter</strong>
          {mode === 'resolved' && <span className="badge" style={{ fontSize: 10, background: 'color-mix(in srgb, var(--good) 16%, transparent)', color: 'var(--good)' }}>Completed</span>}
          <button type="button" onClick={onClose} aria-label="Close" style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--muted)', fontSize: 20, lineHeight: 1, cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <label style={fieldLabel}>To
            <input value={to} onChange={e => setTo(e.target.value)} placeholder="reporter@example.com" style={fieldInput} />
            {to.trim() && !validEmail && <span style={{ color: '#f87171', fontSize: 11 }}>Enter a valid email address.</span>}
          </label>
          <label style={fieldLabel}>Subject
            <input value={subject} onChange={e => setSubject(e.target.value)} style={fieldInput} />
          </label>
          <label style={fieldLabel}>Message
            <textarea value={bodyText} onChange={e => setBodyText(e.target.value)} rows={10} style={{ ...fieldInput, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }} />
          </label>
          {quote && (
            <div className="muted" style={{ fontSize: 11, whiteSpace: 'pre-wrap', maxHeight: 140, overflow: 'auto', padding: '8px 10px', borderRadius: 8, border: '1px dashed var(--border)' }}>
              <div style={{ fontSize: 10, marginBottom: 4 }}>The original report will be included below your message:</div>
              {quote}
            </div>
          )}
          <div className="muted" style={{ fontSize: 11 }}>Sent from your address — replies come straight to you.</div>
          {err && <div style={{ color: '#f87171', fontSize: 12 }}>{err}</div>}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', alignItems: 'center' }}>
            {sent && <span style={{ color: 'var(--good)', fontSize: 12, marginRight: 'auto' }}>Sent ✓</span>}
            {/* Real cancel — only meaningful in resolved mode, where there's a
                completion to undo. Left off the × in the header on purpose:
                that's a generic dismiss, and an accidental click there
                shouldn't un-complete an item. */}
            {!sent && mode === 'resolved' && (
              <button type="button" onClick={onCancelComplete} style={btnGhost}
                title="Don't mark this complete, and don't send an email">
                Cancel
              </button>
            )}
            <button type="button" onClick={onClose} style={btnGhost}>
              {sent ? 'Close' : mode === 'resolved' ? "Complete and don't send" : 'Cancel'}
            </button>
            <button type="button" onClick={send} disabled={!canSend} style={btnPrimary(!canSend)}>
              {sending ? 'Sending…' : sent ? 'Sent' : 'Send email'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Download chips for an inbox email's file attachments. Each links to the
// mode=attachment proxy, which streams the file from func's blob storage. All
// attachments (root or thread) resolve under the same bugReportId.
function fmtBytes(n: number | null): string {
  if (n == null) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1048576).toFixed(1)} MB`;
}
function AttachmentChips({ bugReportId, attachments }: { bugReportId: number; attachments?: AttachmentRef[] }) {
  if (!attachments || !attachments.length) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 2 }}>
      {attachments.map(a => (
        <a key={a.id} href={attachmentDownloadUrl(bugReportId, a.id)} target="_blank" rel="noreferrer"
          title={`Download ${a.name}${a.size ? ` (${fmtBytes(a.size)})` : ''}`}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, maxWidth: 220, textDecoration: 'none', border: '1px solid var(--border)', color: 'var(--accent)', borderRadius: 8, padding: '3px 9px', fontSize: 11 }}>
          <Paperclip size={12} style={{ flexShrink: 0 }} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</span>
        </a>
      ))}
    </div>
  );
}

// Read-only viewer for an email-intake item's rolled-up thread: the ORIGINAL
// message (the card itself — the first mail to the team) followed by every reply
// captured into ContextJson.thread[]. Lazy-loads on open so the board list stays
// lean; mirrors the EmailComposer modal shell.
function ThreadViewer({ report, nameOf, onClose }: {
  report: BugReport; nameOf: (email: string) => string; onClose: () => void;
}) {
  const [msgs, setMsgs] = useState<ThreadMessage[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setMsgs(null); setErr(null);
    fetchThread(report.bugReportId)
      .then(t => { if (!cancelled) setMsgs(t); })
      .catch(e => { if (!cancelled) setErr(e?.message || 'Could not load the thread.'); });
    return () => { cancelled = true; };
  }, [report.bugReportId]);

  const fmt = (iso: string | null) => {
    if (!iso) return '';
    const t = Date.parse(iso);
    return Number.isFinite(t) ? new Date(t).toLocaleString() : '';
  };
  // Appended in arrival order already, but sort defensively so the thread always reads oldest→newest.
  const sorted = msgs ? [...msgs].sort((a, b) => (Date.parse(a.receivedAt || '') || 0) - (Date.parse(b.receivedAt || '') || 0)) : null;
  const total = 1 + (msgs?.length ?? 0);

  const Bubble = ({ who, when, subject, body, tag, attachments }: { who: string; when: string; subject?: string | null; body?: string | null; tag?: string; attachments?: AttachmentRef[] }) => (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px', background: 'rgba(0,0,0,.18)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
        <strong style={{ fontSize: 12.5 }}>{who}</strong>
        {tag && <span className="badge" style={{ fontSize: 9, background: 'rgba(255,255,255,.07)', color: 'var(--muted)' }}>{tag}</span>}
        {when && <span className="muted" style={{ fontSize: 11, marginLeft: 'auto' }}>{when}</span>}
      </div>
      {subject && <div className="muted" style={{ fontSize: 11.5, marginBottom: 4 }}>{subject}</div>}
      <div style={{ fontSize: 12.5, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{body || <span className="muted">(no message text)</span>}</div>
      <AttachmentChips bugReportId={report.bugReportId} attachments={attachments} />
    </div>
  );

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.66)', backdropFilter: 'blur(2px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div className="card" onClick={e => e.stopPropagation()} style={{ width: 'min(640px, 100%)', maxHeight: '90vh', overflow: 'auto', padding: 0, background: 'linear-gradient(180deg, #1a212c, #141922)' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8, position: 'sticky', top: 0, background: '#151b24', zIndex: 1 }}>
          <Mailbox size={16} style={{ color: 'var(--accent)' }} />
          <strong style={{ fontSize: 14 }}>Email thread</strong>
          <span className="badge" style={{ fontSize: 10, background: 'rgba(255,255,255,.07)', color: 'var(--muted)' }}>{total} message{total === 1 ? '' : 's'}</span>
          <button type="button" onClick={onClose} aria-label="Close" style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--muted)', fontSize: 20, lineHeight: 1, cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* The original message — this is the card itself (the first mail to the team). */}
          <Bubble who={nameOf(report.submittedBy)} when={report.when} subject={report.issue} body={report.description || null} tag="Original" attachments={report.attachments} />
          {err && <div style={{ color: '#f87171', fontSize: 12 }}>{err}</div>}
          {!sorted && !err && <div className="muted" style={{ fontSize: 12, textAlign: 'center', padding: 8 }}>Loading replies…</div>}
          {sorted && sorted.map((m, i) => (
            <Bubble key={m.messageId || i} who={m.from ? nameOf(m.from) : '(unknown sender)'} when={fmt(m.receivedAt)} subject={m.subject} body={m.body} attachments={m.attachments} />
          ))}
        </div>
      </div>
    </div>
  );
}

function ReportCard({ r, archived, assignees, nameOf, onStatus, onAssign, onDelete, onEmail, onOpenThread, onSaveNotes, selectMode, selectable, selected, onToggleSelect }: {
  r: BugReport;
  archived?: boolean;
  assignees: string[];
  nameOf: (email: string) => string;
  onStatus: (id: number, s: BugStatus) => void;
  onAssign: (id: number, a: string | null) => void;
  onDelete: (id: number) => void;
  onEmail: (r: BugReport) => void;
  onOpenThread: (r: BugReport) => void;
  onSaveNotes: (id: number, notes: string) => Promise<void>;
  // Merge selection mode: when selectMode && selectable, the whole card becomes a
  // toggle (inner controls inert) and shows a checkbox; clicking selects/deselects.
  selectMode?: boolean;
  selectable?: boolean;
  selected?: boolean;
  onToggleSelect?: (id: number) => void;
}) {
  const accent = archived ? 'var(--muted)' : statusColor(r.status);
  const dim = archived ? 0.6 : (r.status === 'resolved' ? 0.72 : 1);

  // Dev-team notes — an expandable per-card editor available at ANY status.
  const [notesOpen, setNotesOpen] = useState(false);
  const [draft, setDraft] = useState(r.notes || '');
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesErr, setNotesErr] = useState<string | null>(null);
  const hasNotes = !!(r.notes && r.notes.trim());
  const toggleNotes = () => setNotesOpen(o => { const next = !o; if (next) { setDraft(r.notes || ''); setNotesErr(null); } return next; });
  const saveNotesNow = async () => {
    setNotesErr(null); setSavingNotes(true);
    try { await onSaveNotes(r.bugReportId, draft.trim()); setNotesOpen(false); }
    catch (e: any) { setNotesErr(e?.message || 'Could not save notes.'); }
    finally { setSavingNotes(false); }
  };
  const selMode = !!(selectMode && selectable);
  return (
    <div className="card"
      onClick={selMode ? () => onToggleSelect?.(r.bugReportId) : undefined}
      style={{ padding: 0, borderLeft: `3px solid ${accent}`, overflow: 'hidden', opacity: dim, position: 'relative',
        cursor: selMode ? 'pointer' : undefined,
        boxShadow: selMode && selected ? '0 0 0 2px var(--accent)' : undefined }}>
      {selMode && (
        <span style={{ position: 'absolute', top: 8, right: 8, zIndex: 2, color: selected ? 'var(--accent)' : 'var(--muted)', display: 'inline-flex' }}>
          {selected ? <CheckSquare size={18} /> : <Square size={18} />}
        </span>
      )}
      <div style={{ pointerEvents: selMode ? 'none' : undefined }}>
      <div style={{ padding: '12px 16px 8px', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', fontSize: 11.5, color: 'var(--muted)' }}>
        <span className="badge" style={{ fontSize: 10, background: 'rgba(255,255,255,.07)', color: 'var(--muted)' }}>{r.contextApp}</span>
        <span>by {nameOf(r.submittedBy)}</span>
        <span><Clock size={11} style={{ marginRight: 3 }} />{r.when}</span>
        {r.assignedTo && <span>→ {nameOf(r.assignedTo)}</span>}
        {archived && <span className="badge" style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.03em', background: 'rgba(255,255,255,.06)', color: 'var(--muted)' }}>ARCHIVED</span>}
      </div>

      <div style={{ padding: '8px 16px 10px', fontSize: 12.5, lineHeight: 1.5, borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div><strong>Issue:</strong> <span className="muted">{r.issue}</span></div>
        {r.description && <div><strong>Description:</strong> <span className="muted">{r.description}</span></div>}
        {r.steps && <div><strong>Steps to Reproduce:</strong> <span className="muted">{r.steps}</span></div>}
        {r.attachments && r.attachments.length ? (
          <div>
            <strong style={{ fontSize: 11.5 }}>Attachments</strong>
            <AttachmentChips bugReportId={r.bugReportId} attachments={r.attachments} />
          </div>
        ) : null}
        {r.threadCount ? (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 2 }}>
            <button type="button" onClick={() => onOpenThread(r)}
              title={`Open the email thread — ${r.threadCount} ${r.threadCount === 1 ? 'reply' : 'replies'} folded into this item`}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'none', border: '1px solid var(--border)', color: 'var(--accent)', borderRadius: 8, padding: '3px 9px', fontSize: 11, cursor: 'pointer' }}>
              <MoreHorizontal size={14} /> Thread · {r.threadCount}
            </button>
          </div>
        ) : null}
      </div>

      <div style={{ display: 'flex', gap: 10, padding: '8px 16px 12px', borderTop: '1px solid var(--border)', alignItems: 'center', flexWrap: 'wrap' }}>
        <label style={{ fontSize: 11.5, color: 'var(--muted)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          Status
          <select value={r.status} onChange={e => onStatus(r.bugReportId, e.target.value as BugStatus)} style={selectStyle}>
            {STATUSES.map(s => <option key={s} value={s}>{statusLabel(s)}</option>)}
          </select>
        </label>
        <label style={{ fontSize: 11.5, color: 'var(--muted)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          Assignee
          <select value={r.assignedTo ?? ''} onChange={e => onAssign(r.bugReportId, e.target.value || null)} style={selectStyle}>
            <option value="">Unassigned</option>
            {assignees.map(a => <option key={a} value={a}>{nameOf(a)}</option>)}
          </select>
        </label>
        <button type="button" onClick={toggleNotes}
          title="Add or edit dev-team notes"
          style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 5, background: notesOpen ? 'rgba(255,255,255,.06)' : 'none', border: '1px solid var(--border)', color: hasNotes ? 'var(--accent)' : 'var(--muted)', borderRadius: 8, padding: '5px 10px', fontSize: 11.5, cursor: 'pointer' }}>
          <StickyNote size={12} /> Notes{hasNotes ? ' •' : ''}
        </button>
        <button type="button" onClick={() => onEmail(r)}
          title="Email the reporter"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'none', border: '1px solid var(--border)', color: 'var(--muted)', borderRadius: 8, padding: '5px 10px', fontSize: 11.5, cursor: 'pointer' }}>
          <Mail size={12} /> Email
        </button>
        <button type="button" onClick={() => onDelete(r.bugReportId)}
          title="Delete this report — permanent, can't be undone"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'none', border: '1px solid var(--border)', color: 'var(--muted)', borderRadius: 8, padding: '5px 10px', fontSize: 11.5, cursor: 'pointer' }}>
          <Trash2 size={12} /> Delete
        </button>
      </div>

      {notesOpen && (
        <div style={{ padding: '10px 16px 12px', borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <textarea value={draft} onChange={e => setDraft(e.target.value)} rows={4} placeholder="Dev-team notes for this item…"
            style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', borderRadius: 8, border: '1px solid var(--border)', background: 'rgba(0,0,0,.15)', color: 'inherit', fontSize: 12.5, fontFamily: 'inherit', lineHeight: 1.5, padding: '8px 10px' }} />
          {notesErr && <span style={{ color: '#f87171', fontSize: 11 }}>{notesErr}</span>}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {r.notesUpdatedBy && <span className="muted" style={{ fontSize: 10.5 }}>Last edited by {nameOf(r.notesUpdatedBy)}</span>}
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
              <button type="button" onClick={() => setNotesOpen(false)} style={{ ...btnGhost, padding: '5px 12px', fontSize: 11.5 }}>Cancel</button>
              <button type="button" onClick={saveNotesNow} disabled={savingNotes} style={{ ...btnPrimary(savingNotes), padding: '5px 14px', fontSize: 11.5 }}>{savingNotes ? 'Saving…' : 'Save notes'}</button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

// A merge parent (group) card: its own header + lane control, with the child
// cards nested inside (each a full ReportCard, so sub-cards keep every action —
// including "mark complete"). Ungroup dissolves it (children return to the board).
function GroupCard({ group, kids, assignees, nameOf, onStatus, onAssign, onDelete, onEmail, onOpenThread, onSaveNotes, onUngroup }: {
  group: BugReport;
  kids: BugReport[];
  assignees: string[];
  nameOf: (email: string) => string;
  onStatus: (id: number, s: BugStatus) => void;
  onAssign: (id: number, a: string | null) => void;
  onDelete: (id: number) => void;
  onEmail: (r: BugReport) => void;
  onOpenThread: (r: BugReport) => void;
  onSaveNotes: (id: number, notes: string) => Promise<void>;
  onUngroup: (id: number) => void;
}) {
  const [open, setOpen] = useState(true);
  const accent = statusColor(group.status);
  const doneCount = kids.filter(k => k.status === 'resolved').length;
  return (
    <div className="card" style={{ padding: 0, borderLeft: `3px solid ${accent}`, overflow: 'hidden', background: 'color-mix(in srgb, var(--accent) 6%, transparent)' }}>
      <div style={{ padding: '10px 14px', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open}
          title={open ? 'Collapse — hide the grouped cards' : `Expand — show the ${kids.length} grouped cards`}
          style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: '1px solid var(--border)', color: 'var(--muted)', borderRadius: 7, width: 24, height: 24, cursor: 'pointer', flexShrink: 0 }}>
          {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
        </button>
        <button type="button" onClick={() => setOpen(o => !o)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: 0, fontWeight: 700, fontSize: 13, textAlign: 'left', minWidth: 0 }}>
          <Layers size={15} style={{ color: accent, flexShrink: 0 }} />
          {group.issue}
        </button>
        <span className="badge" style={{ fontSize: 10, fontWeight: 700, color: accent, background: `color-mix(in srgb, ${accent} 16%, transparent)` }}>
          {doneCount}/{kids.length} done
        </span>
        <label style={{ fontSize: 11.5, color: 'var(--muted)', display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
          Lane
          <select value={group.status} onChange={e => onStatus(group.bugReportId, e.target.value as BugStatus)} style={selectStyle}>
            {STATUSES.map(s => <option key={s} value={s}>{statusLabel(s)}</option>)}
          </select>
        </label>
        <button type="button" onClick={() => onUngroup(group.bugReportId)}
          title="Ungroup — dissolve this group; the cards return to the board"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'none', border: '1px solid var(--border)', color: 'var(--muted)', borderRadius: 8, padding: '5px 10px', fontSize: 11.5, cursor: 'pointer' }}>
          <X size={12} /> Ungroup
        </button>
      </div>
      {open && (
        <div style={{ padding: '2px 10px 12px 14px', borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 10, borderLeft: `2px solid color-mix(in srgb, ${accent} 40%, transparent)`, marginLeft: 8 }}>
          {kids.length === 0 ? (
            <div className="muted" style={{ fontSize: 12, padding: '10px 4px' }}>No cards in this group.</div>
          ) : (
            kids.map(k => (
              <ReportCard key={k.bugReportId} r={k} assignees={assignees} nameOf={nameOf}
                onStatus={onStatus} onAssign={onAssign} onDelete={onDelete} onEmail={onEmail}
                onOpenThread={onOpenThread} onSaveNotes={onSaveNotes} />
            ))
          )}
        </div>
      )}
    </div>
  );
}

// Dialog shown after selecting cards: name the new parent + pick the lane it lands
// in. onConfirm throws on failure so the error shows here and the dialog stays open.
function MergeDialog({ count, onConfirm, onClose }: {
  count: number; onConfirm: (name: string, status: BugStatus) => Promise<void>; onClose: () => void;
}) {
  const [name, setName] = useState('');
  const [status, setStatus] = useState<BugStatus>('open');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const lanes = STATUSES.filter(s => s !== 'inbox_triage'); // a group in the email-intake lane makes no sense
  const canGo = name.trim().length > 0 && !busy;
  const go = async () => {
    setErr(null); setBusy(true);
    try { await onConfirm(name.trim(), status); }
    catch (e: any) { setErr(e?.message || 'Could not merge the cards.'); setBusy(false); }
  };
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.66)', backdropFilter: 'blur(2px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div className="card" onClick={e => e.stopPropagation()} style={{ width: 'min(460px, 100%)', padding: 0, background: 'linear-gradient(180deg, #1a212c, #141922)' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Layers size={16} style={{ color: 'var(--accent)' }} />
          <strong style={{ fontSize: 14 }}>Merge {count} cards</strong>
          <button type="button" onClick={onClose} aria-label="Close" style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--muted)', fontSize: 20, lineHeight: 1, cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <label style={fieldLabel}>Group name
            <input value={name} onChange={e => setName(e.target.value)} autoFocus placeholder="e.g. Portal login failures" style={fieldInput}
              onKeyDown={e => { if (e.key === 'Enter' && canGo) go(); }} />
          </label>
          <label style={fieldLabel}>Lane
            <select value={status} onChange={e => setStatus(e.target.value as BugStatus)} style={{ ...fieldInput, cursor: 'pointer' }}>
              {lanes.map(s => <option key={s} value={s}>{statusLabel(s)}</option>)}
            </select>
          </label>
          <div className="muted" style={{ fontSize: 11 }}>The {count} selected cards become sub-cards under this one. Each can still be worked and marked complete; ungroup any time.</div>
          {err && <div style={{ color: '#f87171', fontSize: 12 }}>{err}</div>}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={btnGhost}>Cancel</button>
            <button type="button" onClick={go} disabled={!canGo} style={btnPrimary(!canGo)}>{busy ? 'Merging…' : 'Merge'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DevTeam() {
  const { ready, isAdmin, email } = useAuth();
  // Local preview store — the fallback when the backend isn't reachable.
  const { reports: localReports, setStatus: setLocalStatus, setAssignee: setLocalAssignee, setNotes: setLocalNotes, removeReport: removeLocal } = useBugReports();

  const [live, setLive] = useState(false);          // true once the backend answers
  const [loading, setLoading] = useState(true);     // initial board fetch in flight
  const [serverReports, setServerReports] = useState<BugReport[]>([]);
  const [assignees, setAssignees] = useState<string[]>(FALLBACK_ASSIGNEES);
  const [saveErr, setSaveErr] = useState<string | null>(null); // last failed status/assign write
  // previousStatus is only set for mode 'resolved' — what to revert to if the
  // user hits the composer's real Cancel button.
  const [compose, setCompose] = useState<{ report: BugReport; mode: 'resolved' | 'manual'; previousStatus?: BugStatus } | null>(null); // email composer
  const [threadView, setThreadView] = useState<BugReport | null>(null); // rolled-up email thread viewer
  const [mergeMode, setMergeMode] = useState(false);                 // selecting cards to merge
  const [selected, setSelected] = useState<Set<number>>(new Set());  // selected card ids (merge)
  const [mergeDialog, setMergeDialog] = useState(false);             // name/lane dialog open

  const [search, setSearch] = useState('');
  const [appFilter, setAppFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState('all'); // 'all' | 'unassigned' | <email>
  const [reporterEmail, setReporterEmail] = useState('all'); // filter by who filed it ('all' | email)
  const [allStaff, setAllStaff] = useState<PEStaff[]>([]);   // real individual employees for the dropdown
  const [showArchived, setShowArchived] = useState<boolean>(() => {
    try { return localStorage.getItem(SHOW_ARCHIVED_LS) === '1'; } catch { return false; }
  });
  useEffect(() => { try { localStorage.setItem(SHOW_ARCHIVED_LS, showArchived ? '1' : '0'); } catch { /* ignore */ } }, [showArchived]);

  const reload = useCallback(async () => {
    const rows = await fetchBoardReports();
    setServerReports(rows);
    setLive(true);
  }, []);

  useEffect(() => {
    if (!ready || !isAdmin) return;
    let cancelled = false;
    (async () => {
      try {
        const [rows, emails] = await Promise.all([fetchBoardReports(), fetchAssignees().catch(() => [])]);
        if (cancelled) return;
        setServerReports(rows);
        if (emails.length) setAssignees(emails);
        setLive(true);
      } catch {
        if (!cancelled) setLive(false); // backend not ready → preview store
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [ready, isAdmin]);

  // Load the full PE staff directory once, for the Reporter dropdown (any employee).
  useEffect(() => {
    if (!ready || !isAdmin) return;
    let cancelled = false;
    fetch('/api/pe-staff-search?directory=1', { credentials: 'same-origin' })
      .then(r => (r.ok ? r.json() : { staff: [] }))
      .then(d => { if (!cancelled) setAllStaff(d.staff ?? []); })
      .catch(() => { if (!cancelled) setAllStaff([]); });
    return () => { cancelled = true; };
  }, [ready, isAdmin]);

  const reports = live ? serverReports : localReports;

  // Status change: optimistic, then persist (live) or mutate the local store.
  // Moving an item to Completed also pops the email composer so the triager can
  // let the reporter know. The composer's real Cancel button calls this again
  // with the pre-completion status (see the compose render block below) to
  // undo the change — everything else ("Send email" / "Complete and don't
  // send") leaves the completion as-is.
  const onStatus = useCallback((id: number, s: BugStatus) => {
    const prev = reports.find(x => x.bugReportId === id);
    const becameResolved = s === 'resolved' && prev?.status !== 'resolved';
    // Completing a GROUP completes every sub-card too (func cascades server-side;
    // mirror it optimistically). Sub-cards otherwise keep their own status.
    const cascadeKids = !!(prev?.isGroup && s === 'resolved');
    if (!live) { setLocalStatus(id, s); }
    else {
      setSaveErr(null);
      setServerReports(rs => rs.map(x =>
        x.bugReportId === id ? { ...x, status: s }
          : (cascadeKids && x.parentId === id ? { ...x, status: 'resolved' as BugStatus } : x)));
      updateReport(id, { status: s })
        .catch((e: any) => { setSaveErr(e?.message || 'Could not save the status change.'); reload().catch(() => {}); });
    }
    if (becameResolved && prev && !prev.isGroup) setCompose({ report: { ...prev, status: s }, mode: 'resolved', previousStatus: prev.status });
  }, [live, reports, setLocalStatus, reload]);

  const onAssign = useCallback((id: number, a: string | null) => {
    if (!live) { setLocalAssignee(id, a); return; }
    setSaveErr(null);
    const cur = serverReports.find(x => x.bugReportId === id);
    setServerReports(rs => rs.map(x => x.bugReportId === id ? { ...x, assignedTo: a } : x));
    updateReport(id, { status: cur?.status ?? 'open', assignedTo: a })
      .catch((e: any) => { setSaveErr(e?.message || 'Could not save the assignment.'); reload().catch(() => {}); });
  }, [live, serverReports, setLocalAssignee, reload]);

  // "Delete" → permanently delete the report (confirm first — irreversible).
  const onDelete = useCallback((id: number) => {
    if (!window.confirm('Delete this report? This can\'t be undone.')) return;
    if (!live) { removeLocal(id); return; }
    setSaveErr(null);
    setServerReports(rs => rs.filter(x => x.bugReportId !== id));
    deleteReport(id).catch((e: any) => { setSaveErr(e?.message || 'Could not delete the report.'); reload().catch(() => {}); });
  }, [live, removeLocal, reload]);

  // Email icon → open the composer for this report at any status (manual mode).
  const onEmail = useCallback((r: BugReport) => setCompose({ report: r, mode: 'manual' }), []);
  // Thread button → open the rolled-up email conversation for this item.
  const onOpenThread = useCallback((r: BugReport) => setThreadView(r), []);

  // Save dev-team notes (any status): optimistic, then persist (live) or mutate
  // the local store. Returns a promise so the card can show a saving state and
  // surface a failure inline.
  const onSaveNotes = useCallback((id: number, notes: string): Promise<void> => {
    if (!live) { setLocalNotes(id, notes); return Promise.resolve(); }
    setSaveErr(null);
    const stamp = new Date().toISOString();
    setServerReports(rs => rs.map(x => x.bugReportId === id ? { ...x, notes, notesUpdatedBy: email || x.notesUpdatedBy || null, notesUpdatedAt: stamp } : x));
    return saveNotes(id, notes).catch((e: any) => { setSaveErr(e?.message || 'Could not save notes.'); reload().catch(() => {}); throw e; });
  }, [live, email, setLocalNotes, reload]);

  // ── Merge (group cards under one parent) ───────────────────────────────────
  const toggleSelect = useCallback((id: number) => {
    setSelected(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }, []);
  const startMerge = useCallback(() => { setMergeMode(true); setSelected(new Set()); }, []);
  const cancelMerge = useCallback(() => { setMergeMode(false); setSelected(new Set()); setMergeDialog(false); }, []);
  // Create the parent + re-parent the selected children. Throws on failure so the
  // dialog can surface it inline (and stay open); closes everything on success.
  const confirmMerge = useCallback(async (name: string, status: BugStatus) => {
    const childIds = Array.from(selected);
    if (childIds.length < 2) return;
    setSaveErr(null);
    await mergeReports({ name, status, childIds });
    await reload();
    setMergeMode(false); setSelected(new Set()); setMergeDialog(false);
  }, [selected, reload]);
  // Dissolve a group: optimistic (drop the parent, orphan its children back to
  // top-level), then persist; reload on failure to revert.
  const onUngroup = useCallback((parentId: number) => {
    if (!window.confirm('Ungroup this card? The sub-cards return to the board as individual items.')) return;
    setSaveErr(null);
    setServerReports(rs => rs.filter(x => x.bugReportId !== parentId).map(x => x.parentId === parentId ? { ...x, parentId: null } : x));
    ungroupReport(parentId).then(() => reload()).catch((e: any) => { setSaveErr(e?.message || 'Could not ungroup the card.'); reload().catch(() => {}); });
  }, [reload]);

  // Group (parent) rows carry ContextApp='dev-team-group' — never a real app, so
  // keep them out of the App filter's options.
  const apps = useMemo(() => Array.from(new Set(reports.filter(r => r.contextApp !== GROUP_CONTEXT_APP).map(r => r.contextApp))).sort(), [reports]);

  // Merge grouping: parent id → its child cards, and the set of live group ids.
  const childrenByParent = useMemo(() => {
    const m = new Map<number, BugReport[]>();
    for (const r of reports) {
      if (r.parentId != null) { const arr = m.get(r.parentId); if (arr) arr.push(r); else m.set(r.parentId, [r]); }
    }
    return m;
  }, [reports]);
  const groupIds = useMemo(() => new Set(reports.filter(r => r.isGroup).map(r => r.bugReportId)), [reports]);
  // Top-level = not filed under a live group. Orphans (whose group vanished)
  // resurface as top-level so a card is never hidden with no way back.
  const isTopLevel = useCallback((r: BugReport) => r.parentId == null || !groupIds.has(r.parentId), [groupIds]);

  // Dedup by StaffUser + sort by name for the Reporter dropdown options.
  const staffOptions = useMemo(() => {
    const seen = new Set<string>();
    return allStaff
      .filter(s => { const k = (s.staffEmail || '').toLowerCase(); if (!k || seen.has(k)) return false; seen.add(k); return true; })
      .sort((a, b) => a.staffName.localeCompare(b.staffName));
  }, [allStaff]);

  // email → full name (from the PE directory); falls back to the login local-part
  // for anyone not in the directory (or before it loads / if PE is unreachable).
  const nameByEmail = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of allStaff) { const e = (s.staffEmail || '').toLowerCase(); if (e) m.set(e, s.staffName); }
    return m;
  }, [allStaff]);
  const nameOf = useCallback((email: string) => nameByEmail.get((email || '').toLowerCase()) || shortName(email), [nameByEmail]);

  const visible = useMemo(() => {
    let list = reports;
    // Group (parent) cards are containers — never filter them out by app/assignee/
    // reporter (that would hide the group AND everything nested in it). Search still applies.
    const grp = (r: BugReport) => !!r.isGroup;
    if (appFilter !== 'all') list = list.filter(r => grp(r) || r.contextApp === appFilter);
    if (assigneeFilter === 'unassigned') list = list.filter(r => grp(r) || !r.assignedTo);
    else if (assigneeFilter !== 'all') list = list.filter(r => grp(r) || r.assignedTo === assigneeFilter);
    if (reporterEmail !== 'all') list = list.filter(r => grp(r) || reporterMatches(r.submittedBy, reporterEmail));
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(r => r.issue.toLowerCase().includes(q) || (r.submittedBy || '').toLowerCase().includes(q) || nameOf(r.submittedBy).toLowerCase().includes(q) || (r.description || '').toLowerCase().includes(q));
    }
    return list;
  }, [reports, appFilter, assigneeFilter, reporterEmail, search, nameOf]);

  // Archived column shows when the toggle is on OR a search/filter is active — so
  // searching or filtering always surfaces matching archived reports, regardless
  // of the toggle.
  const searching = search.trim().length > 0;
  const filtering = appFilter !== 'all' || assigneeFilter !== 'all' || reporterEmail !== 'all';
  const archivedVisible = showArchived || searching || filtering;
  const columns = archivedVisible ? [...BASE_COLUMNS, ARCHIVED_COLUMN] : BASE_COLUMNS;
  const bucketOf = (r: BugReport) => (isArchived(r) ? 'archived' : r.status);
  // What's actually on screen (archived reports are hidden unless the archived
  // column is showing) — drives the "N of M" count.
  const shown = archivedVisible ? visible : visible.filter(r => !isArchived(r));

  if (!ready) return null;
  if (!isAdmin) {
    return (
      <section className="card">
        <div className="hd"><div className="row"><div style={{ fontWeight: 760 }}>Dev Team</div></div></div>
        <div className="bd"><div className="muted">Admin access required.</div></div>
      </section>
    );
  }

  // Initial board fetch in flight — show a loading panel rather than a momentary
  // empty board (subsequent reloads keep the board visible, they don't set loading).
  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Terminal size={20} style={{ color: 'var(--accent)' }} />
            <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-.01em' }}>Dev Team</div>
          </div>
          <div className="muted" style={{ fontSize: 13, marginTop: 3 }}>User-reported errors across the suite — triage, set status, and assign to a dev.</div>
        </div>
        <div className="card"><div className="bd"><div className="muted">Loading reports…</div></div></div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Terminal size={20} style={{ color: 'var(--accent)' }} />
          <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-.01em' }}>Dev Team</div>
        </div>
        <div className="muted" style={{ fontSize: 13, marginTop: 3 }}>User-reported errors across the suite — triage, set status, and assign to a dev.</div>
      </div>

      {/* preview notice — only when the shared backend isn't reachable yet */}
      {!live && (
        <div className="card" style={{ padding: '10px 14px', borderLeft: '3px solid #fbbf24', fontSize: 12.5 }}>
          <strong style={{ color: '#fbbf24' }}>Preview</strong>{' '}
          <span className="muted">— the Dev Team backend isn't reachable, so this is the in-browser store. Reports here aren't shared across users or persisted.</span>
        </div>
      )}

      {/* save-error banner — a status/assign write didn't persist (card reverted) */}
      {saveErr && (
        <div className="card" style={{ padding: '10px 14px', borderLeft: '3px solid #f87171', fontSize: 12.5 }}>
          <strong style={{ color: '#f87171' }}>Change didn't save</strong>{' '}
          <span className="muted">— {saveErr} The card was reverted.</span>
        </div>
      )}

      {/* controls */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1 1 220px', maxWidth: 320 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)' }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search reports…"
            style={{ width: '100%', padding: '8px 10px 8px 32px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'inherit', fontSize: 13 }} />
        </div>
        <label style={{ fontSize: 12, color: 'var(--muted)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          App
          <select value={appFilter} onChange={e => setAppFilter(e.target.value)} style={selectStyle}>
            <option value="all">All</option>
            {apps.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </label>
        <label style={{ fontSize: 12, color: 'var(--muted)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          Assignee
          <select value={assigneeFilter} onChange={e => setAssigneeFilter(e.target.value)} style={selectStyle}>
            <option value="all">All</option>
            <option value="unassigned">Unassigned</option>
            {assignees.map(a => <option key={a} value={a}>{nameOf(a)}</option>)}
          </select>
        </label>
        <label style={{ fontSize: 12, color: 'var(--muted)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          Reporter
          <select value={reporterEmail} onChange={e => setReporterEmail(e.target.value)} style={selectStyle}>
            <option value="all">All</option>
            {staffOptions.map(s => <option key={s.staffEmail} value={s.staffEmail}>{s.staffName}</option>)}
          </select>
        </label>
        <button type="button" onClick={mergeMode ? cancelMerge : startMerge} disabled={!live}
          title={live ? 'Group selected cards under one parent card' : 'Merging needs the shared backend'}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: mergeMode ? 'color-mix(in srgb, var(--accent) 18%, transparent)' : 'none', border: '1px solid var(--border)', color: mergeMode ? 'var(--accent)' : 'var(--muted)', borderRadius: 8, padding: '7px 12px', fontSize: 12, cursor: live ? 'pointer' : 'not-allowed', opacity: live ? 1 : 0.5 }}>
          <Layers size={14} /> {mergeMode ? 'Cancel merge' : 'Merge'}
        </button>
        <button
          type="button"
          role="switch"
          aria-checked={showArchived}
          onClick={() => setShowArchived(v => !v)}
          title={`Completed reports auto-archive after ${ARCHIVE_DAYS} days. Searching or filtering still surfaces archived reports.`}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginLeft: 'auto', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--muted)', fontSize: 12 }}
        >
          <span style={{ position: 'relative', width: 34, height: 18, borderRadius: 999, background: showArchived ? 'var(--accent)' : 'var(--border)', transition: 'background .15s', flexShrink: 0 }}>
            <span style={{ position: 'absolute', top: 2, left: showArchived ? 18 : 2, width: 14, height: 14, borderRadius: 999, background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,.3)', transition: 'left .15s' }} />
          </span>
          Show archived
        </button>
        <span className="muted" style={{ fontSize: 12 }}>{shown.length} of {reports.length}</span>
      </div>

      {/* merge selection bar — pick cards, then name the group + choose its lane */}
      {mergeMode && (
        <div className="card" style={{ padding: '10px 14px', borderLeft: '3px solid var(--accent)', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <Layers size={16} style={{ color: 'var(--accent)' }} />
          <span style={{ fontSize: 13, fontWeight: 600 }}>Select cards to merge</span>
          <span className="muted" style={{ fontSize: 12 }}>— tap any cards to group them; {selected.size} selected</span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button type="button" onClick={cancelMerge} style={{ ...btnGhost, padding: '6px 12px', fontSize: 12 }}>Cancel</button>
            <button type="button" onClick={() => setMergeDialog(true)} disabled={selected.size < 2} style={{ ...btnPrimary(selected.size < 2), padding: '6px 14px', fontSize: 12 }}>
              Merge {selected.size >= 2 ? `${selected.size} cards` : 'selected'}
            </button>
          </div>
        </div>
      )}

      {/* Kanban columns — one per status, plus Archived when shown */}
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))`, gap: 16, alignItems: 'start' }}>
        {columns.map(colDef => {
          const isArchivedCol = colDef.key === 'archived';
          const col = visible.filter(r => bucketOf(r) === colDef.key && isTopLevel(r));
          const c = colDef.color;
          return (
            <div key={colDef.key} style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 2px 8px', borderBottom: `2px solid ${c}` }}>
                <span style={{ width: 9, height: 9, borderRadius: 999, background: c, flexShrink: 0 }} />
                <span style={{ fontWeight: 700, fontSize: 13 }}>{colDef.label}</span>
                <span className="badge" style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 700, color: c, background: `color-mix(in srgb, ${c} 16%, transparent)` }}>{col.length}</span>
              </div>
              {col.length === 0 ? (
                <div className="muted" style={{ fontSize: 12, padding: '20px 4px', textAlign: 'center', border: '1px dashed var(--border)', borderRadius: 10 }}>None</div>
              ) : (
                col.map(r => r.isGroup ? (
                  <GroupCard key={r.bugReportId} group={r} kids={childrenByParent.get(r.bugReportId) || []} assignees={assignees} nameOf={nameOf} onStatus={onStatus} onAssign={onAssign} onDelete={onDelete} onEmail={onEmail} onOpenThread={onOpenThread} onSaveNotes={onSaveNotes} onUngroup={onUngroup} />
                ) : (
                  <ReportCard key={r.bugReportId} r={r} archived={isArchivedCol} assignees={assignees} nameOf={nameOf} onStatus={onStatus} onAssign={onAssign} onDelete={onDelete} onEmail={onEmail} onOpenThread={onOpenThread} onSaveNotes={onSaveNotes} selectMode={mergeMode} selectable={mergeMode && !r.isGroup} selected={selected.has(r.bugReportId)} onToggleSelect={toggleSelect} />
                ))
              )}
            </div>
          );
        })}
      </div>

      {compose && (
        <EmailComposer
          report={compose.report}
          mode={compose.mode}
          onClose={() => setCompose(null)}
          onCancelComplete={compose.mode === 'resolved' ? () => {
            // Revert the completion — put the item back where it was before,
            // and don't send anything.
            onStatus(compose.report.bugReportId, compose.previousStatus ?? 'open');
            setCompose(null);
          } : undefined}
        />
      )}

      {threadView && (
        <ThreadViewer report={threadView} nameOf={nameOf} onClose={() => setThreadView(null)} />
      )}

      {mergeDialog && (
        <MergeDialog count={selected.size} onConfirm={confirmMerge} onClose={() => setMergeDialog(false)} />
      )}
    </div>
  );
}
